import React, { useState } from 'react';
import './ResultsDisplay.css';
import FavoriteStar from './FavoriteStar';
import { submitFeedback } from '../utils/api';

const TASTE_ATTRS = ['Spice', 'Sour', 'Sweet', 'Bitter', 'Umami', 'Salt', 'Fatty'];
const SMELL_ATTRS = ['Smoky', 'Herbal', 'Citrusy', 'Fermented', 'Roasted', 'Fresh', 'SweetAroma', 'Pungent', 'Earthy'];

// Taste ranks (1–308) → 1.0–10.0 display scale
const TASTE_MAX_RANK = 308;
const rankToTen = (rank) => {
  const v = ((parseFloat(rank) - 1) / (TASTE_MAX_RANK - 1)) * 9 + 1;
  return Math.round(v * 10) / 10; // 1 decimal place
};

function FeedbackForm({ food, currentUser, onFeedbackSent }) {
  const [craving, setCraving]         = useState(5);
  const [discovery, setDiscovery]     = useState(5);
  const [triedBefore, setTriedBefore] = useState(false);
  const [rejected, setRejected]       = useState(false);
  const [comment, setComment]         = useState('');
  const [firstName, setFirstName]     = useState('');
  const [sent, setSent]               = useState(false);
  const [sending, setSending]         = useState(false);

  if (sent) {
    return (
      <div className="feedback-sent">
        ✓ Thanks! Your feedback helps improve recommendations.
      </div>
    );
  }

  const handleSend = async () => {
    setSending(true);
    try {
      await submitFeedback({
        username:     currentUser?.username || '',
        firstName:    currentUser ? '' : firstName,
        foodName:     food['Food Item'],
        cravingFit:   craving,
        discoveryFit: discovery,
        triedBefore,
        saved:        false,
        clicked:      true,
        rejected,
        comment,
      });
      setSent(true);
      if (onFeedbackSent) onFeedbackSent(food['Food Item']);
    } catch (e) {
      console.error('Feedback error', e);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="feedback-form">

      {/* First name for anonymous users */}
      {!currentUser && (
        <div className="feedback-row">
          <label>Your first name <span className="rating-hint">(optional)</span></label>
          <input
            type="text"
            className="feedback-name-input"
            placeholder="e.g. Will"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            maxLength={40}
          />
        </div>
      )}

      <div className="feedback-row">
        <label>
          Craving fit <span className="rating-hint">(how close to what you want right now?)</span>
        </label>
        <div className="rating-input">
          <input
            type="range" min="1" max="10" step="1"
            value={craving}
            onChange={(e) => setCraving(Number(e.target.value))}
            className="rating-slider"
          />
          <span className="rating-val">{craving}/10</span>
        </div>
      </div>

      <div className="feedback-row">
        <label>
          Discovery fit <span className="rating-hint">(would you actually enjoy this?)</span>
        </label>
        <div className="rating-input">
          <input
            type="range" min="1" max="10" step="1"
            value={discovery}
            onChange={(e) => setDiscovery(Number(e.target.value))}
            className="rating-slider discovery-slider"
          />
          <span className="rating-val">{discovery}/10</span>
        </div>
      </div>

      <div className="feedback-checkboxes">
        <label className="feedback-check">
          <input
            type="checkbox"
            checked={triedBefore}
            onChange={(e) => setTriedBefore(e.target.checked)}
          />
          I've tried this before
        </label>
        <label className="feedback-check reject-check">
          <input
            type="checkbox"
            checked={rejected}
            onChange={(e) => setRejected(e.target.checked)}
          />
          Not for me (reject)
        </label>
      </div>

      {/* Free-text comment */}
      <div className="feedback-row">
        <label>Anything else? <span className="rating-hint">(optional)</span></label>
        <textarea
          className="feedback-comment"
          placeholder="e.g. Too spicy for me, or I loved the flavour combination…"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={2}
          maxLength={500}
        />
      </div>

      <button
        className="feedback-submit"
        onClick={handleSend}
        disabled={sending}
      >
        {sending ? 'Saving…' : 'Submit Feedback'}
      </button>
    </div>
  );
}

function ResultsDisplay({ results, onShowRadarChart, currentUser, favorites = [], onToggleFavorite }) {
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  const [feedbackSent, setFeedbackSent]         = useState({});

  if (!results || results.length === 0) return null;

  const handleFeedbackSent = (foodName) => {
    setFeedbackSent(prev => ({ ...prev, [foodName]: true }));
    setExpandedFeedback(null);
  };

  return (
    <div className="results-container">
      <h2>Recommended Foods</h2>
      <p className="results-subtext">
        Rate each recommendation — it only takes a second and teaches the algorithm your taste.
      </p>
      <ul className="food-list">
        {results.map((food, index) => {
          const foodName = food['Food Item'];
          const isFav    = favorites.includes(foodName);
          const scores   = food['_scores'];

          return (
            <li key={index} className="food-item">
              <div className="food-header">
                <strong className="food-name">
                  {index + 1}. {foodName}
                  {currentUser && (
                    <FavoriteStar
                      filled={isFav}
                      onClick={() => onToggleFavorite && onToggleFavorite(foodName, isFav)}
                    />
                  )}
                </strong>
                {scores && scores.exploration >= 0.55 && (
                  <span className="discovery-badge">✦ Discovery Pick</span>
                )}
              </div>

              {/* Taste attributes — shown as 1-10 normalized values */}
              <div className="food-attributes">
                {TASTE_ATTRS.map(attr => food[attr] != null && (
                  <span key={attr} className="attr-chip">
                    {attr}: {rankToTen(food[attr])}
                  </span>
                ))}
                <span className="attr-chip temp-chip">{food['Hot/Cold']}</span>
              </div>

              {/* Aroma attributes — already on 1-10 scale */}
              <div className="food-attributes aroma-attributes">
                {SMELL_ATTRS.map(attr => food[attr] != null && (
                  <span key={attr} className="attr-chip aroma-chip">
                    {attr}: {parseFloat(food[attr]).toFixed(1)}
                  </span>
                ))}
              </div>

              {/* 3 key metrics */}
              {scores && (
                <div className="score-strip">
                  <span className="score-pill" title="Accuracy — how closely this matches your current query">
                    🎯 <strong>{(scores.content * 10).toFixed(1)}</strong>
                    <small>Accuracy</small>
                  </span>
                  <span className="score-pill" title="Trending — alignment with your taste history">
                    📈 <strong>{(scores.learned * 10).toFixed(1)}</strong>
                    <small>Trending</small>
                  </span>
                  <span className="score-pill" title="Originality — how new and undiscovered this is for you">
                    ✨ <strong>{(scores.novelty * 10).toFixed(1)}</strong>
                    <small>Originality</small>
                  </span>
                </div>
              )}

              {/* Feedback — open to everyone */}
              {!feedbackSent[foodName] && (
                <button
                  className="feedback-toggle"
                  onClick={() => setExpandedFeedback(expandedFeedback === foodName ? null : foodName)}
                >
                  {expandedFeedback === foodName ? '▲ Close' : '▼ Rate this recommendation'}
                </button>
              )}

              {expandedFeedback === foodName && (
                <FeedbackForm
                  food={food}
                  currentUser={currentUser}
                  onFeedbackSent={handleFeedbackSent}
                />
              )}

              {feedbackSent[foodName] && (
                <div className="feedback-sent">✓ Feedback recorded</div>
              )}
            </li>
          );
        })}
      </ul>

      {onShowRadarChart && (
        <button onClick={onShowRadarChart} className="radar-chart-button">
          Show Radar Chart
        </button>
      )}
    </div>
  );
}

export default ResultsDisplay;
